import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import type { LexicalEditor, LexicalNode } from 'lexical';
import { $getNodeByKey, $getSelection, $isRangeSelection } from 'lexical';

import { AddIcon } from '@polyai/ui/components/atoms/Icons';
import Tooltip from '@polyai/ui/components/atoms/Tooltip';

import { OPEN_ACTION_PICKER_COMMAND } from 'components/molecules/LexicalEditor/plugins/ActionPicker/commands';
import { getTopLevelNodeKeys } from '../../utils/getTopLevelNodeKeys';
import { setHandlePosition } from '../../utils/setHandlePosition';
import * as Styled from './ActionHandlePlugin.styled';

function useAddActionHandle({
  editor,
  anchorElem,
  isEditable,
  staticHandlePosition,
}: {
  editor: LexicalEditor;
  anchorElem: HTMLElement;
  isEditable: boolean;
  staticHandlePosition?: boolean;
}): React.ReactElement {
  const blockHandleHorizontalOffset = 12;

  const menuRef = useRef<HTMLButtonElement>(null);
  const [cursorElement, setCursorElement] = useState<{
    elem: HTMLElement;
    node: LexicalNode;
  } | null>(null);

  // Track cursor position changes
  useEffect(() => {
    const updateCursorPosition = () => {
      // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
      editor.getEditorState().read(() => {
        const selection = $getSelection();

        if (!$isRangeSelection(selection)) {
          return;
        }

        const anchorNode = selection.anchor.getNode();
        let targetNode = anchorNode;

        // Find the top-level paragraph node
        while (targetNode) {
          const parent = targetNode.getParent();
          if (!parent || parent.getKey() === 'root') {
            break;
          }
          targetNode = parent;
        }

        if (targetNode) {
          const element = editor.getElementByKey(
            targetNode.getKey(),
          ) as HTMLElement | null;

          if (element && targetNode) {
            setCursorElement({
              elem: element,
              node: targetNode,
            });
          }
        }
      });
    };

    // Listen for selection changes
    const removeSelectionListener = editor.registerUpdateListener(
      ({ editorState }) => {
        editorState.read(() => {
          updateCursorPosition();
        });
      },
    );

    // Initial cursor position
    updateCursorPosition();

    return () => {
      removeSelectionListener();
    };
  }, [editor]);

  // Initialize cursor position when there's no selection
  useEffect(() => {
    if (cursorElement === null) {
      const topLevelNodeKeys = getTopLevelNodeKeys(editor);

      if (topLevelNodeKeys.length > 0) {
        const firstKey = topLevelNodeKeys[0];

        // Read the actual LexicalNode from editor state
        const firstNode = editor.read(() => {
          return $getNodeByKey(firstKey);
        });

        // Grab its DOM element
        const element = editor.getElementByKey(firstKey) as HTMLElement | null;

        if (firstNode && element) {
          setCursorElement({ elem: element, node: firstNode });
        }
      }
    }
  }, [editor, cursorElement]);

  // Position the button based on cursor position or static position
  useEffect(() => {
    if (menuRef.current) {
      if (staticHandlePosition) {
        // Position at bottom right corner of the input
        const menuButton = menuRef.current;

        menuButton.style.bottom = '12px';
        menuButton.style.right = '12px';
        menuButton.style.top = 'auto';
        menuButton.style.left = 'auto';
        menuButton.style.transform = 'none';
      } else if (cursorElement?.node) {
        // Follow cursor position
        setHandlePosition(
          cursorElement?.elem,
          menuRef.current,
          anchorElem,
          blockHandleHorizontalOffset,
        );
      }
    }
  }, [anchorElem, cursorElement, staticHandlePosition]);

  const handleAddClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      const getAnchor = () => {
        if (cursorElement && cursorElement.node) {
          return {
            element: cursorElement.elem,
            node: cursorElement.node,
          };
        }

        const element = editor.getRootElement();

        return {
          element,
        };
      };

      const anchor = getAnchor();

      editor.dispatchCommand(OPEN_ACTION_PICKER_COMMAND, {
        anchorElement: menuRef.current!,
        paragraphNodeKey: anchor?.node?.getKey(),
      });

      event.stopPropagation();
      event.preventDefault();
    },
    [editor, cursorElement],
  );

  if (!isEditable) {
    return <React.Fragment />;
  }

  return createPortal(
    <Styled.AddButton
      ref={menuRef}
      aria-label="Add action"
      data-test-id="add-function-button"
      type="button"
      onClick={event => {
        handleAddClick(event);
      }}
    >
      <Tooltip
        content={
          <>
            Add action{' '}
            <Styled.ShortcutHint>or press &quot;/&quot;</Styled.ShortcutHint>
          </>
        }
      >
        <AddIcon />
      </Tooltip>
    </Styled.AddButton>,
    anchorElem,
  );
}

export function ActionHandlePlugin({
  anchorElem = document.body,
  staticHandlePosition,
}: {
  anchorElem?: HTMLElement;
  staticHandlePosition?: boolean;
}): React.ReactElement {
  const [editor] = useLexicalComposerContext();
  return useAddActionHandle({
    editor,
    anchorElem,
    isEditable: editor._editable,
    staticHandlePosition,
  });
}
